import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Field, getFormValues, getFormMeta } from "redux-form";
import { ReduxControls, ErrorBanner } from "rhombus-react";
import classNames from "classnames";

import { saveConcept, addNewCreativeTagsToConcept } from "reducers/ConceptDetails";
import { requiredNotNull } from "widgets/forms/validators/validators";
import { buildDisplayTagSizes } from "./SelectCreativeTypePane";
import { noop } from "utils/common";

import "./PartnerCreativePane.css";

function validate(value, allValues, props, name) {
	const { registeredFields = {} } = props || {};

	if(registeredFields[name]?.count > 0){
		return requiredNotNull(value);
	}
}

export default function PartnerCreativePane(props) {
	const dispatch = useDispatch()
		, [submitErrors, setSubmitErrors] = useState([])
		, {
			libraryId,
			handleSubmit,
			setIsLoading=noop,
			setApiErrors,
			formErrors,
			valid,
			formName,
			handleSubmitSuccess,
			initialValues = {},
			isNewConcept = true,
			conceptId,
		} = props
		, { baseSizes = {}, customSizes = [], meta } = useSelector((state) => {
			const meta = getFormMeta(formName)(state) || {}
				, values = getFormValues(formName)(state) || {}
				, baseSizes = values?.['base-sizes'] || {}
				, customSizes = values?.['custom-sizes'] || [];

			return {
				meta,
				customSizes,
				baseSizes,
			};
		});

	const sizes = useMemo(() => {
		return buildDisplayTagSizes(baseSizes, customSizes, initialValues);
	}, [baseSizes, customSizes]);

	function handleSubmitFail(response) {
		const { description = "" } = response?.payload?.errorObj || {};
		setSubmitErrors([description]);
		setApiErrors(true);
	}

	function removeUnselectedSizes(formValues, sizes) {
		const { "display-tags": displayTags = {} } = formValues || {};

		for(let size in displayTags) {
			if(!sizes.includes(size)) {
				delete displayTags[size];
			}
		}

		return {
			...formValues,
			"display-tags": displayTags,
		};
	}

	const createConcept = handleSubmit((values) => {
		setIsLoading(true);
		dispatch(saveConcept(libraryId, buildHtmlTagSizes(values, libraryId))).then((resp) => {
			setIsLoading(false);
			if(!resp?.success) {
				handleSubmitFail(resp);
			}else {
				handleSubmitSuccess();
			}
		});
	});

	const updateConcept = handleSubmit((values) => {
		setIsLoading(true);
		const filteredValues = removeUnselectedSizes(values, sizes);
		dispatch(addNewCreativeTagsToConcept(libraryId, conceptId, buildHtmlTagSizes(filteredValues, libraryId))).then((resp) => {
			setIsLoading(false);
			if(!resp?.success) {
				handleSubmitFail(resp);
			}else {
				handleSubmitSuccess();
			}
		});
	});

	function renderHeader() {
		let errorMessages = [];
		const { errors: { "display-tags": displayTagErrors = {} } } = formErrors || { errors: {} }
			, { "display-tags": displayTagsMeta } = meta || {};

		if(displayTagErrors && displayTagsMeta) {
			errorMessages = Object.keys(displayTagErrors).reduce((errors, size) => {
				if(displayTagsMeta?.[size]?.touched) {
					errors.push(`${size} - ${displayTagErrors[size]}`);
				}
				return errors;
			}, []);
		}

		if(submitErrors.length) {
			errorMessages = [...errorMessages, ...submitErrors];
		}

		if(errorMessages.length) {
			return <ErrorBanner title="Sorry, please fix the errors below and try again." messages={errorMessages} />;
		}

		return (
			<header className="header-info">
				<strong>Add creative html for each size</strong>
			</header>
		);
	}

	function renderInput(size) {
		return (
			<div key={`input-${size}`} className="html-input-container">
				<Field
					component={ReduxControls.Input}
					label={size}
					name={`display-tags.${size}`}
					type="textarea"
					onChange={() => setApiErrors(false)}
					validate={[validate]}
				/>
			</div>
		);
	}

	return (
		<div className="partner-creative-tags">
			{ renderHeader() }
			<form className="partner-creative-form" id={formName} onSubmit={isNewConcept ? createConcept : updateConcept}>
				<div className="form-headings">
					<h3>Placement Size</h3>
					<h3 className={classNames({ "is-valid": formErrors !== undefined && valid })}>HTML</h3>
				</div>
				{
					sizes.map(size => renderInput(size))
				}
			</form>
		</div>
	);
}

function buildHtmlTagSizes(values, libraryId) {
	let conceptData =  {
		conceptName: values.conceptName,
		conceptType: "HTML",
		creativeLibraryId: libraryId,
		sizes: [],
		displayHtmlTagConcept: true,
	};

	Object.keys(values?.['display-tags']).forEach((size) => {
		const [width, height] = size.split('x');
		conceptData.sizes.push({
			width: parseInt(width),
			height: parseInt(height),
			displayHtmlTagCode: values?.['display-tags']?.[size],
		});
	});

	return conceptData;
}
